import { getMaybeString } from './helpers';
import {
  DEFAULT_FUNNEL_LABEL,
  LEGACY_FUNNEL_LABEL,
  type FunnelKind,
  type FunnelOption,
} from './types';

export const LEAD_FUNNEL_OPTION: FunnelOption = {
  label: DEFAULT_FUNNEL_LABEL,
  kind: 'lead',
  entityTypeId: null,
  dealCategoryId: null,
};

export function normalizeFunnelLabel(label: string) {
  if (label === LEGACY_FUNNEL_LABEL) {
    return DEFAULT_FUNNEL_LABEL;
  }
  return label;
}

export function findFunnelOption(
  options: FunnelOption[],
  label: string
): FunnelOption | undefined {
  const normalized = normalizeFunnelLabel(label);
  return options.find(
    (option) => option.label === normalized || option.label === label
  );
}

function uniqueFunnelOptions(options: FunnelOption[]) {
  const seen = new Set<string>();
  return options.filter((option) => {
    if (seen.has(option.label)) {
      return false;
    }
    seen.add(option.label);
    return true;
  });
}

export function mapSmartProcessFunnelOptions(raw: ANY[]): FunnelOption[] {
  const mapped = (Array.isArray(raw) ? raw : [])
    .map((funnel: ANY) => ({
      label:
        funnel?.Title ?? funnel?.title ?? funnel?.NAME ?? funnel?.name ?? '',
      kind: 'smart_process' as FunnelKind,
      entityTypeId:
        typeof funnel?.EntityTypeID === 'number'
          ? funnel.EntityTypeID
          : typeof funnel?.entityTypeId === 'number'
            ? funnel.entityTypeId
            : null,
      dealCategoryId: null,
    }))
    .filter((option) => Boolean(option.label) && option.entityTypeId !== null);

  return uniqueFunnelOptions(mapped as FunnelOption[]);
}

export function mapDealFunnelOptions(raw: ANY[]): FunnelOption[] {
  const mapped = (Array.isArray(raw) ? raw : [])
    .map((funnel: ANY) => {
      const label =
        getMaybeString(funnel?.name ?? funnel?.Name) ||
        getMaybeString(funnel?.title ?? funnel?.Title) ||
        '';
      const id = funnel?.id ?? funnel?.ID;

      return {
        label,
        kind: 'deal' as FunnelKind,
        entityTypeId: null,
        dealCategoryId: typeof id === 'number' ? id : null,
      };
    })
    .filter(
      (option) => Boolean(option.label) && option.dealCategoryId !== null
    );

  return uniqueFunnelOptions(mapped as FunnelOption[]);
}

export function buildMeetingFunnelOptions(
  smartProcessRaw: ANY[],
  dealFunnelsRaw: ANY[]
): FunnelOption[] {
  return [
    LEAD_FUNNEL_OPTION,
    ...mapSmartProcessFunnelOptions(smartProcessRaw),
    ...mapDealFunnelOptions(dealFunnelsRaw),
  ];
}

export function getFunnelSelectionKey(option: FunnelOption) {
  return `${option.kind}:${option.entityTypeId ?? ''}:${option.dealCategoryId ?? ''}:${option.label}`;
}

export const FUNNEL_GROUP_LABELS: Record<FunnelKind, string> = {
  lead: 'Лид',
  smart_process: 'Смарт-процесс',
  deal: 'Воронка',
};

export type MeetingFunnelOptionGroup = {
  label: string;
  options: string[];
};

export function buildMeetingFunnelOptionGroups(
  funnelOptions: FunnelOption[]
): MeetingFunnelOptionGroup[] {
  const byKind: Record<FunnelKind, string[]> = {
    lead: [],
    smart_process: [],
    deal: [],
  };

  for (const option of funnelOptions) {
    if (!byKind[option.kind].includes(option.label)) {
      byKind[option.kind].push(option.label);
    }
  }

  return (['lead', 'smart_process', 'deal'] as FunnelKind[])
    .map((kind) => ({
      label: FUNNEL_GROUP_LABELS[kind],
      options: byKind[kind],
    }))
    .filter((group) => group.options.length > 0);
}
